import { GridSortModel } from "@mui/x-data-grid";
import {
  OrderLine,
  OrderLineItemStatusCountResponse,
} from "../../../../api/orders/orderInterfaces";
import {
  OrdersByLineItemFilter,
  UpdateSelectedAccountsPayload,
} from "../types";

export enum OrdersByLineItemActionsType {
  SET_ORDER_LINES = "SET_ORDER_LINES",
  SET_IS_LOADING = "SET_IS_LOADING",
  SET_IS_DOWNLOADING = "SET_IS_DOWNLOADING",
  SET_IS_TECHNICAL_ERROR = "SET_IS_TECHNICAL_ERROR",
  SET_IS_AUTHORIZATION_ERROR = "SET_IS_AUTHORIZATION_ERROR",
  SET_IS_AUTHENTICATION_ERROR = "SET_IS_AUTHENTICATION_ERROR",
  SET_IS_DOWNLOAD_FAILED = "SET_IS_DOWNLOAD_FAILED",
  SET_FILTER = "SET_FILTER",
  SET_CURRENT_PAGE = "SET_CURRENT_PAGE",
  SET_PAGE_ROW_SIZE = "SET_PAGE_ROW_SIZE",
  UPDATE_SELECTED_ACCOUNTS = "UPDATE_SELECTED_ACCOUNTS",
  SET_SORT_MODEL = "SET_SORT_MODEL",
  SET_ORDER_STATUS_COUNTS = "SET_ORDER_STATUS_COUNTS",
  SET_ORDER_LINES_STATUS_COUNT_LOADING = "SET_ORDER_LINES_STATUS_COUNT_LOADING",
  SET_TAB_INDEX = "SET_TAB_INDEX",
  SET_ORDER_STATUS_COUNT_FOR_TAB = "SET_ORDER_STATUS_COUNT_FOR_TAB",
  SET_TAB_DOWNLOADING = "SET_TAB_DOWNLOADING",
  SET_TAB_DOWNLOAD_FAILED = "SET_TAB_DOWNLOAD_FAILED",
}

export type SetOrderStatusCountForTabAction = {
  type: OrdersByLineItemActionsType.SET_ORDER_STATUS_COUNT_FOR_TAB;
  payload: { tabIndex: number; count: any };
};

export function setOrderStatusCountForTab(
  tabIndex: number,
  count: any
): SetOrderStatusCountForTabAction {
  return {
    type: OrdersByLineItemActionsType.SET_ORDER_STATUS_COUNT_FOR_TAB,
    payload: { tabIndex, count },
  };
}

type SetOrderLinesAction = {
  type: OrdersByLineItemActionsType.SET_ORDER_LINES;
  payload: {
    orderLines: OrderLine[];
    totalOrderLines: number;
  };
};

type SetOrderStatusCount = {
  type: OrdersByLineItemActionsType.SET_ORDER_STATUS_COUNTS;
  payload: OrderLineItemStatusCountResponse;
};

type SetIsLoadingAction = {
  type: OrdersByLineItemActionsType.SET_IS_LOADING;
  payload: boolean;
};

type SetIsOrderLineStatusCountLoading = {
  type: OrdersByLineItemActionsType.SET_ORDER_LINES_STATUS_COUNT_LOADING;
  payload: { tabIndex: number; isLoading: boolean };
};

type SetIsDownloadingAction = {
  type: OrdersByLineItemActionsType.SET_IS_DOWNLOADING;
  payload: boolean;
};

type SetIsTechnicalErrorAction = {
  type: OrdersByLineItemActionsType.SET_IS_TECHNICAL_ERROR;
  payload: boolean;
};

type SetIsDownloadFailedErrorAction = {
  type: OrdersByLineItemActionsType.SET_IS_DOWNLOAD_FAILED;
  payload: boolean;
};

type SetIsAuthorizationErrorAction = {
  type: OrdersByLineItemActionsType.SET_IS_AUTHORIZATION_ERROR;
  payload: boolean;
};

type SetIsAuthenticationErrorAction = {
  type: OrdersByLineItemActionsType.SET_IS_AUTHENTICATION_ERROR;
  payload: boolean;
};

type SetFilterAction = {
  type: OrdersByLineItemActionsType.SET_FILTER;
  payload: OrdersByLineItemFilter;
};

type SetPageRowSizeAction = {
  type: OrdersByLineItemActionsType.SET_PAGE_ROW_SIZE;
  payload: number;
};

type SetCurrentPageAction = {
  type: OrdersByLineItemActionsType.SET_CURRENT_PAGE;
  payload: number;
};

type SetSortModelAction = {
  type: OrdersByLineItemActionsType.SET_SORT_MODEL;
  payload: GridSortModel;
};

type UpdateSelectedAccountsAction = {
  type: OrdersByLineItemActionsType.UPDATE_SELECTED_ACCOUNTS;
  payload: UpdateSelectedAccountsPayload;
};

type SetTabIndexAction = {
  type: OrdersByLineItemActionsType.SET_TAB_INDEX;
  payload: number;
};

export type TabName =
  | "AllLines"
  | "Backorders"
  | "OnHold"
  | "Investigations"
  | "SentToSupplier"
  | "SentToFactory";

type SetTabDownloadingAction = {
  type: OrdersByLineItemActionsType.SET_TAB_DOWNLOADING;
  payload: {
    tab: TabName;
    isDownloading: boolean;
  };
};

type SetTabDownloadFailedAction = {
  type: OrdersByLineItemActionsType.SET_TAB_DOWNLOAD_FAILED;
  payload: {
    tab: TabName;
    isDownloadFailed: boolean;
  };
};

export type OrdersByLineItemActions =
  | SetOrderLinesAction
  | SetIsLoadingAction
  | SetIsDownloadingAction
  | SetIsDownloadFailedErrorAction
  | SetIsTechnicalErrorAction
  | SetIsAuthenticationErrorAction
  | SetIsAuthorizationErrorAction
  | SetFilterAction
  | SetPageRowSizeAction
  | SetCurrentPageAction
  | UpdateSelectedAccountsAction
  | SetSortModelAction
  | SetOrderStatusCount
  | SetIsOrderLineStatusCountLoading
  | SetTabIndexAction
  | SetOrderStatusCountForTabAction
  | SetTabDownloadingAction
  | SetTabDownloadFailedAction;

export function setOrderLinesAction(payload: {
  orderLines: OrderLine[];
  totalOrderLines: number;
}): SetOrderLinesAction {
  return {
    type: OrdersByLineItemActionsType.SET_ORDER_LINES,
    payload,
  };
}

export function setOrderLinesStatusCountLoading(
  tabIndex: number,
  isLoading: boolean
): SetIsOrderLineStatusCountLoading {
  return {
    type: OrdersByLineItemActionsType.SET_ORDER_LINES_STATUS_COUNT_LOADING,
    payload: { tabIndex, isLoading },
  };
}

export function setOrderStatusCountAction(
  payload: OrderLineItemStatusCountResponse
): SetOrderStatusCount {
  return {
    type: OrdersByLineItemActionsType.SET_ORDER_STATUS_COUNTS,
    payload,
  };
}

export function setIsLoadingAction(isLoading: boolean): SetIsLoadingAction {
  return {
    type: OrdersByLineItemActionsType.SET_IS_LOADING,
    payload: isLoading,
  };
}

export function setIsDownloadingAction(
  isDownloading: boolean
): SetIsDownloadingAction {
  return {
    type: OrdersByLineItemActionsType.SET_IS_DOWNLOADING,
    payload: isDownloading,
  };
}

export function setIsTechnicalErrorAction(
  isTechnicalError: boolean
): SetIsTechnicalErrorAction {
  return {
    type: OrdersByLineItemActionsType.SET_IS_TECHNICAL_ERROR,
    payload: isTechnicalError,
  };
}

export function setIsDownloadFailedErrorAction(
  isDownloadFailed: boolean
): SetIsDownloadFailedErrorAction {
  return {
    type: OrdersByLineItemActionsType.SET_IS_DOWNLOAD_FAILED,
    payload: isDownloadFailed,
  };
}

export function setIsAuthorizationErrorAction(
  isAuthorizationError: boolean
): SetIsAuthorizationErrorAction {
  return {
    type: OrdersByLineItemActionsType.SET_IS_AUTHORIZATION_ERROR,
    payload: isAuthorizationError,
  };
}

export function setIsAuthenticationErrorAction(
  isAuthenticationError: boolean
): SetIsAuthenticationErrorAction {
  return {
    type: OrdersByLineItemActionsType.SET_IS_AUTHENTICATION_ERROR,
    payload: isAuthenticationError,
  };
}

export function setFilterAction(
  ordersByLineItemFilter: OrdersByLineItemFilter
): SetFilterAction {
  return {
    type: OrdersByLineItemActionsType.SET_FILTER,
    payload: ordersByLineItemFilter,
  };
}

export function setPageRowSizeAction(
  pageRowSize: number
): SetPageRowSizeAction {
  return {
    type: OrdersByLineItemActionsType.SET_PAGE_ROW_SIZE,
    payload: pageRowSize,
  };
}

export function setCurrentPageAction(
  currentPage: number
): SetCurrentPageAction {
  return {
    type: OrdersByLineItemActionsType.SET_CURRENT_PAGE,
    payload: currentPage,
  };
}

export function setSortModelAction(
  sortModel: GridSortModel
): SetSortModelAction {
  return {
    type: OrdersByLineItemActionsType.SET_SORT_MODEL,
    payload: sortModel,
  };
}

export function setTabIndexAction(tabIndex: number): SetTabIndexAction {
  return {
    type: OrdersByLineItemActionsType.SET_TAB_INDEX,
    payload: tabIndex,
  };
}

export function updateSelectedAccountsAction(
  updateSelectedAccountsPayload: UpdateSelectedAccountsPayload
): UpdateSelectedAccountsAction {
  return {
    type: OrdersByLineItemActionsType.UPDATE_SELECTED_ACCOUNTS,
    payload: updateSelectedAccountsPayload,
  };
}
export function setTabDownloadingAction(
  tab: TabName,
  isDownloading: boolean
): SetTabDownloadingAction {
  return {
    type: OrdersByLineItemActionsType.SET_TAB_DOWNLOADING,
    payload: { tab, isDownloading },
  };
}

export function setTabDownloadFailedAction(
  tab: TabName,
  isDownloadFailed: boolean
): SetTabDownloadFailedAction {
  return {
    type: OrdersByLineItemActionsType.SET_TAB_DOWNLOAD_FAILED,
    payload: { tab, isDownloadFailed },
  };
}
